import { ELoadingType, EMGirlType, Loading, MgirlNotice } from '@bilibili-maoer/prelude'
import { format } from '@bilibili-maoer/web-utils'

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Breadcrumbs, Divider, Typography, styled } from '@mui/material'
import { match } from 'ts-pattern'

import { EAnnouncementType } from './types'

import { useAppSelector } from '~/app/hooks'
import { StyledPaper as Paper } from '~/core/components/layout'
import { StyledTitleLink } from '~/core/components/link'
import { Title, TitleChildren } from '~/core/components/title'
import { UserApi } from '~/services/fm/user'
import { NewsApi } from '~/services/web/news'
import useAppNavigate from '~/shared/navigation/use-app-navigate'

const StyledPaper = styled(Paper)`
  width: 968px;
  margin: 0 auto;
  @media screen and (min-width: 1400px) {
    width: 1140px;
  }
`
const StyledBreadcrumbsContainer = styled('div')`
  margin-bottom: '20px';
`
const FormattedMgirlNotFound = styled(MgirlNotice)`
  margin-top: 140px;
`
const StyledContentTitleContainer = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 20px;
  padding: 30px 20px;
`
const StyledContentContainer = styled('div')`
  width: 100%;
  padding: 30px 20px;
`
const StyledSpan = styled('span')`
  color: #bdbdbd; /** $grey-lighter */
`

export const AnnouncementDetail = ({
  parentPageRoute,
  parentPageTitle,
  pageTitle,
  documentTitle,
}: {
  parentPageRoute: string
  parentPageTitle: string
  pageTitle: string
  documentTitle?: TitleChildren
}) => {
  const [tobeRefetchNotice, setTobeRefetchNotice] = useState(false)
  const { id } = useParams()
  const { data: userInfo } = UserApi.getUserInfo.useQuery()
  const idNumber = parseInt(id || '0')
  const { currentListUnreadIds } = useAppSelector((state) => state.NotificationNotices)
  const navigate = useAppNavigate()
  const [getLiveData, { data: livePostData, isFetching: islivePostDataFetching }] = NewsApi.getLivePost.useLazyQuery()
  const [getNotice] = NewsApi.getNotice.useLazyQuery()

  useEffect(() => {
    if (!idNumber || Number.isNaN(idNumber)) {
      navigate(parentPageRoute)
    }
    getLiveData({ id: idNumber })
  }, [getLiveData, idNumber, navigate, parentPageRoute])
  useEffect(() => {
    // livePostData 获取到之后会删除 currentListUnreadIds 中相应的 id，所以需要在删除之前判断 id 是否未读
    if (userInfo?.user && islivePostDataFetching && currentListUnreadIds && currentListUnreadIds.includes(idNumber)) {
      setTobeRefetchNotice(true)
    }
  }, [currentListUnreadIds, idNumber, islivePostDataFetching, userInfo?.user])
  useEffect(() => {
    if (livePostData?.success && tobeRefetchNotice) {
      getNotice({
        tag_ids: [EAnnouncementType.LiveGuildTag, EAnnouncementType.LiveLiverTag, EAnnouncementType.LiveTag],
      })
      setTobeRefetchNotice(false)
    }
  }, [getNotice, livePostData?.success, tobeRefetchNotice])
  const title = documentTitle || livePostData?.info.title
  return (
    <StyledPaper>
      {title && <Title>{title}</Title>}
      <StyledBreadcrumbsContainer>
        <Breadcrumbs aria-label="breadcrumb" sx={{ color: '#000' }}>
          <StyledTitleLink
            onClick={() => (parentPageRoute ? navigate(parentPageRoute) : navigate(-1))}
            sx={{
              color: '#757575',
              // 和公告列表的 Title 的 line-height 保持一致
              lineHeight: '36px',
            }}
          >
            {parentPageTitle}
          </StyledTitleLink>
          <Typography
            variant="h5"
            sx={{
              fontWeight: '700',
              fontSize: '24px',
              // 和公告列表的 Title 的 line-height 保持一致
              lineHeight: '36px',
            }}
          >
            {pageTitle}
          </Typography>
        </Breadcrumbs>
      </StyledBreadcrumbsContainer>
      {match(livePostData)
        .with(undefined, () => <Loading type={ELoadingType.Cat} />)
        .otherwise((livePostData) =>
          livePostData.success && livePostData.info ? (
            <>
              <StyledContentTitleContainer>
                <Typography variant="h6" sx={{ fontWeight: '700', fontSize: '18px', marginBottom: '10px' }}>
                  {livePostData.info.title}
                </Typography>
                <StyledSpan>
                  {format.formatUTCTimeBySeconds(parseInt(livePostData.info.ctime), 'YYYY-MM-DD HH:mm:ss')}
                </StyledSpan>
              </StyledContentTitleContainer>
              <Divider />
              <StyledContentContainer
                dangerouslySetInnerHTML={{
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  __html: livePostData.info.news,
                }}
              />
            </>
          ) : (
            <FormattedMgirlNotFound
              image={EMGirlType.NotFound}
              description={typeof livePostData.info === 'string' ? livePostData.info : '网页出现错误~'}
            />
          )
        )}
    </StyledPaper>
  )
}
